// src/components/backgroundcheck/BackgroundCheckCard.jsx
import React, { useState } from 'react';
import { backgroundCheckAPI } from '../../services/backgroundCheckAPI';
import './style/BackgroundCheckCard.css';

const BackgroundCheckCard = ({ backgroundCheck, onRefresh }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      PENDING_CONSENT: 'badge-warning',
      ASSIGNED: 'badge-info',
      IN_PROGRESS: 'badge-info',
      COMPLETED: 'badge-success',
      ON_HOLD: 'badge-warning',
      UNABLE_TO_VERIFY: 'badge-danger',
    };
    return statusClasses[status] || 'badge-secondary';
  };

  const getResultBadgeClass = (result) => {
    const resultClasses = {
      PASS: 'badge-success',
      FAIL: 'badge-danger',
      CONDITIONAL: 'badge-warning',
    };
    return resultClasses[result] || 'badge-secondary';
  };

  const formatScope = (scope) => {
  if (!Array.isArray(scope) || scope.length === 0) return 'No scope defined';
  const scopeLabels = {
    criminal: 'Criminal History',
    employment: 'Employment Verification',
    education: 'Education Verification',
    credit: 'Credit Check',
    drug: 'Drug Screening',
    reference: 'Reference Check',
  };
  return scope.map(s => scopeLabels[s] || s).join(', ');
};


  const handleViewReport = async () => {
    if (!backgroundCheck.reportUrl) return;
    
    setLoading(true);
    try {
      const reportData = await backgroundCheckAPI.getReport(backgroundCheck.id);
      window.open(reportData.reportUrl, '_blank');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAudit = async () => {
    setLoading(true);
    try {
      const auditData = await backgroundCheckAPI.getAudit(backgroundCheck.id);
      console.log('Audit trail:', auditData);
      // You can show this in a modal or separate view
      alert('Check console for audit trail');
    } catch (err) {
      setError(err.message || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-check-card">
      {/* Header */}
      <div className="bg-check-header">
        <div className="bg-check-candidate">
          <div className="candidate-avatar">
            {backgroundCheck.candidate?.profile?.firstName?.[0] || 'C'}
          </div>
          <div className="candidate-info">
            <h3 className="candidate-name">
              {backgroundCheck.candidate?.profile?.firstName}{' '}
              {backgroundCheck.candidate?.profile?.lastName}
            </h3>
            <p className="candidate-email">
              {backgroundCheck.candidate?.email}
            </p>
          </div>
        </div>

        <div className="bg-check-status-badges">
          <span className={`status-badge ${getStatusBadgeClass(backgroundCheck.status)}`}>
            {backgroundCheck.status.replace(/_/g, ' ')}
          </span>
          {backgroundCheck.result && (
            <span className={`result-badge ${getResultBadgeClass(backgroundCheck.result)}`}>
              {backgroundCheck.result}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-check-body">
        <div className="bg-check-info-grid">
          <div className="info-item">
            <span className="info-label">Agency</span>
            <span className="info-value">
              {backgroundCheck.agency?.profile?.firstName}{' '}
              {backgroundCheck.agency?.profile?.lastName}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Assigned Date</span>
            <span className="info-value">
              {formatDate(backgroundCheck.assignedAt)}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Completed Date</span>
            <span className="info-value">
              {formatDate(backgroundCheck.completedAt)}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Consent Given</span>
            <span className="info-value">
              {backgroundCheck.consentGiven ? '✓ Yes' : '✗ No'}
            </span>
          </div>
        </div>

        {/* Scope */}
        <div className="bg-check-scope">
          <h4>Background Check Scope</h4>
          <p className="scope-list">{formatScope(backgroundCheck.scope)}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠</span>
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="bg-check-actions">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="btn btn-secondary btn-sm"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>

          {backgroundCheck.reportUrl && (
            <button
              onClick={handleViewReport}
              disabled={loading}
              className="btn btn-primary btn-sm"
            >
              {loading ? 'Loading...' : 'View Report'}
            </button>
          )}

          <button
            onClick={handleViewAudit}
            disabled={loading}
            className="btn btn-secondary btn-sm"
          >
            Audit Trail
          </button>
        </div>

        {/* Details Section */}
        {showDetails && (
          <div className="bg-check-details">
            <h4>Additional Details</h4>
            
            {backgroundCheck.comments && (
              <div className="detail-section">
                <strong>Comments:</strong>
                <p>{backgroundCheck.comments}</p>
              </div>
            )}

            <div className="detail-section">
              <strong>Background Check ID:</strong>
              <p className="monospace">{backgroundCheck.id}</p>
            </div>

            <div className="detail-section">
              <strong>Created:</strong>
              <p>{formatDate(backgroundCheck.createdAt)}</p>
            </div>

            <div className="detail-section">
              <strong>Last Updated:</strong>
              <p>{formatDate(backgroundCheck.updatedAt)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackgroundCheckCard;
